import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useI18n, fill } from "@/i18n";
import Seo from "@/components/Seo";
import TransferCta from "@/components/TransferCta";
import { eventBySlug, EVENTS } from "@/data/events";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { ArrowLeft, CalendarBlank, MapPin, CheckCircle } from "@phosphor-icons/react";

export default function EventDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const e = eventBySlug(slug);
  if (!e) { navigate("/events"); return null; }
  const c = e[lang] || e.de;
  const month = (e.month && (e.month[lang] || e.month.de)) || "";

  const title = lang === "de"
    ? `${c.name} – Flughafentransfer ab Zürich (ZRH)`
    : `${c.name} – Airport Transfer from Zürich (ZRH)`;
  const desc = `${c.intro} ${c.why}`;
  const faqs = [
    { q: fill(t("ev_faq1_q"), { name: c.name }), a: fill(t("ev_faq1_a"), { name: c.name }) },
    { q: fill(t("ev_faq2_q"), { name: c.name }), a: fill(t("ev_faq2_a"), { name: c.name, month }) },
    { q: t("ev_faq3_q"), a: t("ev_faq3_a") },
  ];

  const benefits = [t("ev_ben_1"), t("ev_ben_2"), t("ev_ben_3"), t("dben_247")];

  const related = EVENTS.filter((x) => x.slug !== e.slug && x.region === e.region).slice(0, 3);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="event-detail-page">
      <Seo title={title} description={desc} path={`/events/${e.slug}`}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_events"), path: "/events" }, { name: c.name, path: `/events/${e.slug}` }]),
          { "@context": "https://schema.org", "@type": "Event", name: c.name, description: desc, image: e.image, eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode", location: { "@type": "Place", name: c.name, address: { "@type": "PostalAddress", addressRegion: e.region, addressCountry: "CH" } } },
          faqSchema(faqs)]} />

      <Link to="/events" className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {t("events_all")}</Link>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-body text-xs font-semibold text-[#EF5A1E] bg-[#EEF1FF] rounded-full px-3 py-1">{e.region}</span>
        {month && <span className="font-body text-xs font-semibold text-[#0B1533] bg-[#F5F2ED] rounded-full px-3 py-1 inline-flex items-center gap-1.5"><CalendarBlank size={13} /> {month}</span>}
      </div>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight mt-3">{c.name}</h1>
      {e.image && (
        <div className="mt-6 rounded-3xl overflow-hidden h-64 sm:h-80 bg-[#0B1533]">
          <img src={e.image} alt={c.name} className="w-full h-full object-cover" />
        </div>
      )}
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-6">{c.intro}</p>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-3">{c.why}</p>

      <div className="mt-10"><TransferCta cityId={e.cityId} dropoffName={c.name} testid="event-cta" /></div>

      <div className="grid sm:grid-cols-2 gap-3 mt-10">
        {benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#F5F2ED] px-5 py-4">
            <CheckCircle size={20} weight="fill" className="text-[#EF5A1E] shrink-0" />
            <span className="font-body text-sm text-[#0B1533]">{b}</span>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("dfaq_h2")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`event-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("events_related")}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/events/${r.slug}`} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
                <span className="flex items-center gap-2.5 font-body text-sm text-[#0B1533]"><MapPin size={16} className="text-[#EF5A1E]" /> {(r[lang] || r.de).name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
